import type {
  DrawerActionsConfig,
  LeftPanelConfig,
  RightPanelConfig,
} from '@openx/components-core/lib/SelectionDrawer/types.ts';
import isEqual from 'lodash/isEqual';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormSegment, GroupLike, LookalikeSegmentDefinition } from '../../../../../types.ts';
import { sortSegmentsByExpiration } from '../../../../../utils/sortSegmentsByExpiration.ts';
import type { DrawerState, DrawerTypeValue } from '../../../drawerTypes.ts';
import { createDrawerActionsConfig } from '../utils/createDrawerActionsConfig.ts';
import { isFormSegment, isFormSegmentSelectionReturnType, isLookalikeSegmentDefinition } from '../utils.ts';
import type { LookalikeDefinitionSegments } from './lookalike/lookalikeSegmentsReducer.ts';
import type { UseDrawerActions, UseLookalikeSegmentsSelectionReturn, UseSegmentSelectionReturn } from './types.ts';

type UseAudienceDrawerAdapterProps = {
  currentDrawerType: DrawerTypeValue;
  drawerState: Extract<DrawerState, { isOpen: true }>;
  setDrawerState: Dispatch<SetStateAction<DrawerState>>;
  segmentsGroup: GroupLike;
  segmentSelectionProps: UseSegmentSelectionReturn | UseLookalikeSegmentsSelectionReturn;
  enforceMarketUnionAfterApply: (fieldPath: string) => boolean;
  totalCount: number;
  loadingSegments: boolean;
  paginationResult: {
    loadingMore: boolean;
    handleLoadMore: () => void;
    allSegments: (FormSegment | LookalikeSegmentDefinition)[];
  };
  lookalikeModal: {
    handleLalDefinitionSelect: (definition: LookalikeSegmentDefinition) => void;
  };
  useDrawerActions: UseDrawerActions;
};

type UseAudienceDrawerAdapterReturn = {
  leftPanel: Omit<LeftPanelConfig<FormSegment>, 'search'>;
  rightPanel: RightPanelConfig<FormSegment | LookalikeDefinitionSegments>;
  actions: DrawerActionsConfig;
  discardConfig: {
    show: boolean;
    onConfirm: () => void;
    onCancel: () => void;
  };
};

export const useAudienceDrawerAdapter = ({
  currentDrawerType,
  drawerState,
  setDrawerState,
  segmentsGroup,
  segmentSelectionProps,
  enforceMarketUnionAfterApply,
  totalCount,
  loadingSegments,
  paginationResult,
  lookalikeModal,
  useDrawerActions,
}: UseAudienceDrawerAdapterProps): UseAudienceDrawerAdapterReturn => {
  const { t } = useTranslation();

  const lastAppliedSegments = useRef(segmentSelectionProps.allSelectedSegments);
  const hasSelectedItemsChanged = useMemo(
    () => !isEqual(segmentSelectionProps.allSelectedSegments, lastAppliedSegments.current),
    [segmentSelectionProps.allSelectedSegments]
  );

  const drawerActionsConfig = useMemo(
    () =>
      createDrawerActionsConfig({
        currentDrawerType,
        drawerState,
        setDrawerState,
        segmentsGroup,
        segmentSelectionProps,
        hasSelectedItemsChanged,
        enforceMarketUnionAfterApply,
      }),
    [
      currentDrawerType,
      drawerState,
      setDrawerState,
      segmentsGroup,
      segmentSelectionProps,
      hasSelectedItemsChanged,
      enforceMarketUnionAfterApply,
    ]
  );

  const { showDiscardModal, onApply, onDiscardConfirm, onDiscardCancel, onClose } =
    useDrawerActions(drawerActionsConfig);

  const handleItemSelect = (item: FormSegment | LookalikeSegmentDefinition) => {
    if (isFormSegment(item) && isFormSegmentSelectionReturnType(segmentSelectionProps)) {
      segmentSelectionProps.handleSegmentSelection(item);
    }

    if (isLookalikeSegmentDefinition(item)) {
      lookalikeModal.handleLalDefinitionSelect(item);
    }
  };

  const selectedIds = useMemo(() => {
    if (isFormSegmentSelectionReturnType(segmentSelectionProps)) {
      return new Set(segmentSelectionProps.allSelectedSegments.map(segment => segment.id));
    }

    // For lookalike segments, use the definition IDs
    return new Set((segmentSelectionProps as UseLookalikeSegmentsSelectionReturn).selectedDefinitionIds);
  }, [segmentSelectionProps]);

  const getItemId = (item: FormSegment) => item.id;

  const leftPanelTitle = currentDrawerType === 'lookalike' ? t('Lookalike Definitions') : t('Segments List');

  const leftPanel: Omit<LeftPanelConfig<FormSegment>, 'search'> = {
    title: leftPanelTitle,
    items: paginationResult.allSegments as FormSegment[],
    totalCount,
    selectedIds,
    onItemSelect: handleItemSelect,
    getItemId,
    pagination: {
      hasMore: paginationResult.allSegments.length < totalCount,
      isLoading: paginationResult.loadingMore,
      onLoadMore: paginationResult.handleLoadMore,
    },
    emptyStateMessage: t('No segments found'),
    loading: loadingSegments,
  };

  // For lookalike mode, we need to pass definitions instead of individual segments
  const rightPanelItems = isFormSegmentSelectionReturnType(segmentSelectionProps)
    ? // Display only: expired and soon to expire segments first, the selection itself keeps its order
      sortSegmentsByExpiration(segmentSelectionProps.allSelectedSegments)
    : // For lookalike, map definitions to items and filter out missing entries
      (segmentSelectionProps as UseLookalikeSegmentsSelectionReturn).definitionOrder
        .map(
          definitionId =>
            (segmentSelectionProps as UseLookalikeSegmentsSelectionReturn).segmentsByDefinition[definitionId]
        )
        .filter((item): item is LookalikeDefinitionSegments => item !== undefined);

  const rightPanelOnItemRemove = isFormSegmentSelectionReturnType(segmentSelectionProps)
    ? (item: FormSegment | LookalikeDefinitionSegments) => {
        segmentSelectionProps.handleSegmentRemoval(item as FormSegment);
      }
    : (item: FormSegment | LookalikeDefinitionSegments) => {
        // For lookalike, item is a LookalikeDefinitionSegments
        const definitionId = (item as LookalikeDefinitionSegments).similaritySegment?.lookalike_definition_id;
        if (definitionId) {
          (segmentSelectionProps as UseLookalikeSegmentsSelectionReturn).removeDefinition(definitionId);
        }
      };

  const rightPanelGetItemId = isFormSegmentSelectionReturnType(segmentSelectionProps)
    ? (item: FormSegment | LookalikeDefinitionSegments) => (item as FormSegment).id
    : (item: FormSegment | LookalikeDefinitionSegments) =>
        (item as LookalikeDefinitionSegments).similaritySegment?.lookalike_definition_id || '';

  const rightPanel: RightPanelConfig<FormSegment | LookalikeDefinitionSegments> = {
    title: t('Selected Segments'),
    items: rightPanelItems as (FormSegment | LookalikeDefinitionSegments)[],
    onItemRemove: rightPanelOnItemRemove,
    getItemId: rightPanelGetItemId,
    onRemoveAll: segmentSelectionProps.handleRemoveAll,
    selectedCount: segmentSelectionProps.selectedCount, // Use actual segment count (not definition count for lookalike)
  };

  const actions: DrawerActionsConfig = {
    onApply,
    onClose,
    applyLabel: t('apply selected {countString}', {
      countString: segmentSelectionProps.allSelectedSegments.length
        ? `[ ${segmentSelectionProps.allSelectedSegments.length} ]`
        : '',
    }),
    applyDisabled: !hasSelectedItemsChanged,
  };

  const discardConfig = {
    show: showDiscardModal && hasSelectedItemsChanged,
    onConfirm: onDiscardConfirm,
    onCancel: onDiscardCancel,
  };

  return {
    leftPanel,
    rightPanel,
    actions,
    discardConfig,
  };
};
